import { useState, useEffect, useCallback } from 'react';
import galleryData from '../data/gallery.json';

export interface GalleryImage {
  id: number;
  title: string;
  category: string;
  /** jeden soubor nebo více souborů (např. svatby) */
  filename: string | string[];
  alt: string;
  likes: number;
  views: number;
  featured: boolean;
  date: string; // ISO string
}

export interface GalleryCategory {
  id: string;
  name: string;
  description: string;
}

export const useGallery = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      setImages(galleryData.images as unknown as GalleryImage[]);
      setCategories(galleryData.categories as unknown as GalleryCategory[]);
      setLoading(false);
    } catch {
      setError('Chyba pri načítaní galérie');
      setLoading(false);
    }
  }, []);

  /** Vrátí první soubor (pokud je pole), jinak přímou hodnotu. */
  const getFirstFilename = useCallback((file: string | string[]): string => {
    return Array.isArray(file) ? (file[0] ?? '') : file;
  }, []);

  /**
   * Vrátí finální URL pro obrázek.
   * - "http" → nechá bez změny
   * - "/"    → nechá bez změny (absolutní cesta v public)
   * - jinak prefixne "/images/gallery/"
   */
  const getImageUrl = useCallback((filename: string): string => {
    if (!filename) return (galleryData as any).placeholder || 'https://picsum.photos/800/600?random=999';
    if (filename.startsWith('http') || filename.startsWith('/')) return filename;
    return `/images/gallery/${filename}`;
  }, []);

  /** Pohodlná kombinace: vezme první soubor a udělá finální URL. */
  const resolveImageSrc = useCallback(
    (file: string | string[]): string => getImageUrl(getFirstFilename(file)),
    [getFirstFilename, getImageUrl]
  );

  const getPlaceholderUrl = useCallback((): string => {
    return (galleryData as any).placeholder || 'https://picsum.photos/800/600?random=999';
  }, []);

  const getImagesByCategory = useCallback((categoryId: string): GalleryImage[] => {
    if (categoryId === 'all') return images;
    return images.filter((img) => img.category === categoryId);
  }, [images]);

  const getFeaturedImages = useCallback((): GalleryImage[] => {
    return images.filter((img) => img.featured);
  }, [images]);

  const getImagesByDate = useCallback((limit?: number): GalleryImage[] => {
    const sorted = [...images].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    return limit ? sorted.slice(0, limit) : sorted;
  }, [images]);

  // (volitelné – držím kvůli kompatibilitě)
  const incrementLikes = useCallback((imageId: number) => {
    setImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, likes: img.likes + 1 } : img))
    );
  }, []);

  const incrementViews = useCallback((imageId: number) => {
    setImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, views: img.views + 1 } : img))
    );
  }, []);

  return {
    images,
    categories,
    loading,
    error,
    // helpers
    getFirstFilename,
    getImageUrl,
    resolveImageSrc,
    getPlaceholderUrl,
    getImagesByCategory,
    getFeaturedImages,
    getImagesByDate,
    incrementLikes,
    incrementViews,
  };
};